import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Users, Globe, Send, ArrowLeft, Mail, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/common";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
      <div className="flex items-center justify-between"><span className="text-sm text-slate-400">{label}</span><Icon className="h-4 w-4 text-amber-400" /></div>
      <div className="display mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [tab, setTab] = useState("customers");
  const [checking, setChecking] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/domains").then((r) => setDomains(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/customers/${id}/status`, { status }); toast.success(`Customer ${status}`); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const forceVerify = async (id) => {
    setChecking(id);
    try { const { data } = await api.post(`/admin/domains/${id}/verify`); data.verified ? toast.success("DNS verified") : toast.info("DNS requirements are still pending"); load(); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setChecking(null); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      <header className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-[#0B0F17]/80 px-5 py-4 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-[#0B0F17]"><Shield className="h-5 w-5" /></div>
          <span className="display text-lg font-bold">Admin Command Center</span>
        </div>
        <div className="flex items-center gap-4">
          <Link data-testid="admin-customer-view" to="/app" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Customer view</Link>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="text-sm text-slate-400 hover:text-red-400">Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 p-6 fade-up">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Users} label="Customers" value={stats.customers} />
            <Stat icon={Globe} label="Domains" value={`${stats.verified_domains}/${stats.domains}`} />
            <Stat icon={Send} label="Emails sent" value={stats.emails_sent.toLocaleString()} />
            <Stat icon={Mail} label="Received" value={stats.emails_received.toLocaleString()} />
          </div>
        )}

        <Link data-testid="admin-delivery-settings-link" to="/admin/delivery" className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:underline"><Send className="h-4 w-4" />Delivery & automation settings</Link>
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-900/40 p-1 w-fit">
          {["customers", "domains"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`mono rounded-md px-4 py-1.5 text-xs capitalize transition-colors ${tab === t ? "bg-amber-500/15 text-amber-400" : "text-slate-400 hover:text-white"}`}>{t}</button>
          ))}
        </div>

        {tab === "customers" && (
          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
            <table className="responsive-admin-table w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Domains</th><th className="px-5 py-3">Sent</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {customers.map((c) => (
                  <tr key={c.id} data-testid={`admin-customer-row-${c.id}`} className="hover:bg-slate-800/40">
                    <td className="px-5 py-3"><div className="font-medium">{c.name}</div><div className="mono text-xs text-slate-500">{c.email}</div></td>
                    <td className="px-5 py-3"><span className={`mono text-xs ${c.role === "admin" ? "text-amber-400" : "text-slate-400"}`}>{c.role}</span></td>
                    <td className="px-5 py-3">{c.domain_count}</td>
                    <td className="px-5 py-3">{c.email_count}</td>
                    <td className="px-5 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-5 py-3 text-right">
                      {c.role !== "admin" && (c.status === "active" ? (
                        <button onClick={() => setStatus(c.id, "suspended")} data-testid={`admin-suspend-${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-red-400 hover:underline"><Ban className="h-3.5 w-3.5" /> Suspend</button>
                      ) : (
                        <button onClick={() => setStatus(c.id, "active")} data-testid={`admin-activate-${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400 hover:underline"><CheckCircle2 className="h-3.5 w-3.5" /> Activate</button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "domains" && (
          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
            <table className="responsive-admin-table w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-3">Domain</th><th className="px-5 py-3">Owner</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {domains.map((d) => (
                  <tr key={d.id} data-testid={`admin-domain-row-${d.id}`} className="hover:bg-slate-800/40">
                    <td className="mono px-5 py-3">{d.domain}</td>
                    <td className="mono px-5 py-3 text-xs text-slate-400">{d.owner_email}</td>
                    <td className="px-5 py-3"><StatusPill status={d.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <button disabled={checking === d.id} onClick={() => forceVerify(d.id)} data-testid={`admin-check-dns-${d.id}`} className="text-xs font-medium text-emerald-400 hover:underline disabled:opacity-50">{checking === d.id ? "Checking…" : "Check DNS"}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
